import { useMemo, useState } from 'react';
import { Button, InputNumber, Loader, Message, Popover, SelectPicker, Tooltip, Whisper } from 'rsuite';
import { Modal } from '@nsi-advisory-services/components';

import {
  useCloseDates,
  useProjectPaceSettings,
  useTaskActualsForMonths,
  useTaskHourForecastForMonths,
  useTaskOrderActualsForMonths,
  useTaskOrderHourForecastForMonths,
  useUsers,
  useUpdateProjectPaceSettings,
  useUsersTimeOffDays,
} from '../hooks/useQueries';

import { HOURS_PER_DAY, buildTimeOffHoursByDay, buildWorkingDays, toIsoDay } from '../utils/capacity';
import usePermissions from '../hooks/usePermissions';
import './ActualsPaceModal.css';

/*
 * Pace for the current month: for each person, how much of their forecast they have
 * logged compared with how much of the month has gone by.
 *
 * "How much of the month has gone by" is measured in that person's own available hours,
 * not calendar days: weekdays less company close dates, less their time off, scaled by
 * their FTE. Someone who was on PTO for the first week is not behind for having logged
 * nothing that week. Only days before today count as elapsed, since actuals are pulled
 * overnight and today's hours are not in yet.
 */

// Pace is (share of forecast logged) / (share of month elapsed). Within the project's
// tolerance of 1.0 the person is on pace; below it they are under pace, above it over.
// Both are problems in equal measure: running over burns hours the forecast did not
// plan for just as running under leaves them unspent. The
// band is saved per project (Pace settings) because teams log hours differently.
const DEFAULT_TOLERANCE_PCT = 10;

const toThresholds = (settings) => {
  const behind = Number(settings?.behindTolerancePct);
  const ahead = Number(settings?.aheadTolerancePct);
  const behindPct = Number.isFinite(behind) ? behind : DEFAULT_TOLERANCE_PCT;
  const aheadPct = Number.isFinite(ahead) ? ahead : DEFAULT_TOLERANCE_PCT;
  return { behindPct, aheadPct, behindBelow: 1 - behindPct / 100, aheadAbove: 1 + aheadPct / 100 };
};

const formatBand = ({ behindPct, aheadPct }) =>
  behindPct === aheadPct ? `\u00b1${behindPct}%` : `\u2212${behindPct}% / +${aheadPct}%`;

const STATUS = {
  cantReach: { key: 'cantReach', label: "Can't reach", icon: 'bi-x-octagon', tone: 'danger', rank: 0 },
  over: { key: 'over', label: 'Over forecast', icon: 'bi-exclamation-triangle', tone: 'danger', rank: 1 },
  short: { key: 'short', label: 'Fell short', icon: 'bi-arrow-down-circle', tone: 'warn', rank: 2 },
  behind: { key: 'behind', label: 'Under pace', icon: 'bi-chevron-double-down', tone: 'warn', rank: 2 },
  ahead: { key: 'ahead', label: 'Over pace', icon: 'bi-chevron-double-up', tone: 'warn', rank: 2 },
  unplanned: { key: 'unplanned', label: 'Unplanned', icon: 'bi-question-circle', tone: 'warn', rank: 3 },
  onPace: { key: 'onPace', label: 'On pace', icon: 'bi-check-circle', tone: 'good', rank: 5 },
  met: { key: 'met', label: 'Met forecast', icon: 'bi-check-circle', tone: 'good', rank: 5 },
  early: { key: 'early', label: 'Too early', icon: 'bi-clock', tone: 'neutral', rank: 6 },
};

const STATUS_ORDER = Object.values(STATUS).sort((a, b) => a.rank - b.rank);

// The per-person pace bars (and their legend) are hidden for now; the numeric columns carry the
// same information. Flip this to bring them back.
const SHOW_PACE_BARS = false;

const SORT_OPTIONS = [
  { label: 'Needs attention first', value: 'status' },
  { label: 'Pace (slowest first)', value: 'pace' },
  { label: 'Hours left to log', value: 'remaining' },
  { label: 'Name', value: 'name' },
];

const pad2 = (n) => String(n).padStart(2, '0');

const getCurrentMonthStartIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-01`;
};

const addMonthsIso = (monthStartIso, delta) => {
  const [y, m] = String(monthStartIso).split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
};

const formatMonthTitle = (monthStartIso) => {
  const [y, m] = String(monthStartIso).split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '';
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
};

const formatHrs = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 1 });
};

// "12 hrs", "1 hr": hours with their unit spelled out.
const hrsText = (value) => {
  const text = formatHrs(value);
  return `${text} ${text === '1' ? 'hr' : 'hrs'}`;
};

const formatPct = (fraction) => {
  const n = Number(fraction);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
};

const formatUpdatedAt = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const computePace = ({ projected, actual, workingDays, todayIso, timeOffByDay, fteScale, teamElapsedFraction, isPastMonth, thresholds }) => {
  let capacityTotal = 0;
  let capacityElapsed = 0;
  let capacityRemaining = 0;
  let remainingDays = 0;
  let timeOffHours = 0;

  for (const iso of workingDays) {
    const off = Math.min(HOURS_PER_DAY, Math.max(0, Number(timeOffByDay?.[iso] ?? 0)));
    timeOffHours += off;
    const available = (HOURS_PER_DAY - off) * fteScale;
    capacityTotal += available;
    if (iso < todayIso) {
      capacityElapsed += available;
    } else {
      capacityRemaining += available;
      if (available > 0) remainingDays += 1;
    }
  }

  const elapsedFraction = capacityTotal > 0 ? capacityElapsed / capacityTotal : teamElapsedFraction;
  const expected = projected * elapsedFraction;
  const remainingToLog = Math.max(0, projected - actual);
  const maxReachable = actual + capacityRemaining;

  // Two ways the month can finish: keep going at the rate logged so far, or log at the
  // planned rate from here on. The truth is usually somewhere between, so the pair is
  // shown as a range, capped by the time the person actually has left.
  let low = projected;
  let high = projected;
  if (elapsedFraction > 0) {
    const atCurrentRate = actual / elapsedFraction;
    const atPlannedRate = actual + projected * (1 - elapsedFraction);
    low = Math.min(maxReachable, Math.min(atCurrentRate, atPlannedRate));
    high = Math.min(maxReachable, Math.max(atCurrentRate, atPlannedRate));
  }
  low = Math.max(actual, low);
  high = Math.max(low, high);

  const pace = expected > 0 ? actual / expected : null;

  // A finished month has no pace left to judge, only whether the forecast was hit.
  let status;
  if (projected <= 0) status = STATUS.unplanned;
  else if (isPastMonth) status = actual > projected ? STATUS.over : pace < thresholds.behindBelow ? STATUS.short : STATUS.met;
  else if (actual > projected) status = STATUS.over;
  else if (remainingToLog > capacityRemaining + 0.01) status = STATUS.cantReach;
  else if (pace === null) status = STATUS.early;
  else if (pace < thresholds.behindBelow) status = STATUS.behind;
  else if (pace > thresholds.aheadAbove) status = STATUS.ahead;
  else status = STATUS.onPace;

  return {
    elapsedFraction,
    expected,
    pace,
    low,
    high,
    remainingToLog,
    capacityRemaining,
    remainingDays,
    neededPerDay: remainingDays > 0 && remainingToLog > 0 ? remainingToLog / remainingDays : null,
    timeOffHours,
    status,
  };
};

// Two concentric rings: the outer one is how far through the month the team is, the
// inner one how much of the forecast is logged. When the inner ring trails the outer,
// the team is behind. Each is drawn as a true arc path from 12 o'clock, clockwise.
const describeArc = (cx, cy, r, fraction) => {
  const f = Math.max(0, Math.min(1, Number(fraction) || 0));
  if (f <= 0) return null;
  if (f >= 0.9999) return { circle: true };
  const angle = 2 * Math.PI * f;
  const x = cx + r * Math.sin(angle);
  const y = cy - r * Math.cos(angle);
  return { d: `M ${cx} ${cy - r} A ${r} ${r} 0 ${f > 0.5 ? 1 : 0} 1 ${x.toFixed(3)} ${y.toFixed(3)}` };
};

function RingArc({ cx, cy, r, fraction, className }) {
  const arc = describeArc(cx, cy, r, fraction);
  if (!arc) return null;
  return arc.circle ? <circle className={className} cx={cx} cy={cy} r={r} /> : <path className={className} d={arc.d} />;
}

function PaceRing({ elapsed, complete, tone }) {
  const size = 132;
  const stroke = 11;
  const gap = 5;
  const c = size / 2;
  const outerR = (size - stroke) / 2;
  const innerR = outerR - stroke - gap;

  return (
    <svg className="ppc-pace-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label={`Month elapsed ${formatPct(elapsed)}, forecast complete ${formatPct(complete)}`}>
      <g fill="none" strokeWidth={stroke} strokeLinecap="round">
        <circle className="ppc-pace-ring__track" cx={c} cy={c} r={outerR} />
        <RingArc className="ppc-pace-ring__elapsed" cx={c} cy={c} r={outerR} fraction={elapsed} />
        <circle className="ppc-pace-ring__track" cx={c} cy={c} r={innerR} />
        <RingArc className={`ppc-pace-ring__complete ppc-pace-tone--${tone}`} cx={c} cy={c} r={innerR} fraction={complete} />
      </g>
      <text x="50%" y="47%" textAnchor="middle" className="ppc-pace-ring__value">{formatPct(complete)}</text>
      <text x="50%" y="62%" textAnchor="middle" className="ppc-pace-ring__caption">of forecast</text>
    </svg>
  );
}

function StatusChip({ status }) {
  return (
    <span className={`ppc-pace-chip ppc-pace-tone--${status.tone}`}>
      <i className={`bi ${status.icon}`} aria-hidden="true" />
      {status.label}
    </span>
  );
}

function RowTooltip({ row }) {
  const p = row.pace;
  return (
    <div className="ppc-pace-tip">
      <div className="ppc-pace-tip__title">{row.name}</div>
      <dl>
        <dt>Forecast</dt><dd>{hrsText(row.projected)}</dd>
        <dt>Logged so far</dt><dd>{hrsText(row.actual)}</dd>
        {row.projected > 0 ? (
          <>
            <dt>Expected by now</dt><dd>{hrsText(p.expected)} ({formatPct(p.elapsedFraction)} of the month)</dd>
            <dt>Likely finish</dt><dd>{formatHrs(p.low)}–{hrsText(p.high)}</dd>
            <dt>Left to log</dt>
            <dd>
              {hrsText(p.remainingToLog)}
              {p.neededPerDay ? ` · ${formatHrs(p.neededPerDay)} hrs/day over ${p.remainingDays} day${p.remainingDays === 1 ? '' : 's'}` : ''}
            </dd>
          </>
        ) : null}
        <dt>Time available</dt><dd>{hrsText(p.capacityRemaining)} left this month (all projects)</dd>
        {p.timeOffHours > 0 ? (<><dt>Time off</dt><dd>{hrsText(p.timeOffHours)} this month</dd></>) : null}
      </dl>
    </div>
  );
}

function PaceBar({ row, domain }) {
  const p = row.pace;
  const toPct = (hours) => `${Math.max(0, Math.min(1, hours / row.projected / domain)) * 100}%`;
  const actualOverflows = row.actual / row.projected > domain;

  return (
    <div className="ppc-pace-bar" aria-label={`${row.name}: ${formatHrs(row.actual)} of ${formatHrs(row.projected)} hours, expected ${formatHrs(p.expected)} by now`}>
      <div className="ppc-pace-bar__track" />
      <div className={`ppc-pace-bar__range ppc-pace-tone--${p.status.tone}`}
        style={{ left: toPct(p.low), width: `calc(${toPct(p.high)} - ${toPct(p.low)})` }} />
      <div className={`ppc-pace-bar__fill ppc-pace-tone--${p.status.tone}`} style={{ width: toPct(row.actual) }} />
      {actualOverflows ? <i className="bi bi-chevron-double-right ppc-pace-bar__overflow" aria-hidden="true" /> : null}
      <div className="ppc-pace-bar__target" style={{ left: toPct(row.projected) }} />
      <div className="ppc-pace-bar__expected" style={{ left: toPct(p.expected) }} />
    </div>
  );
}

export default function ActualsPaceModal({ open, onClose, projectId, taskOrderId, taskId = null, scopeLabel = '' }) {
  const currentMonthStart = useMemo(() => getCurrentMonthStartIso(), [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [monthStart, setMonthStart] = useState(currentMonthStart);
  const isCurrentMonth = monthStart === currentMonthStart;
  const isPastMonth = monthStart < currentMonthStart;
  const months = useMemo(() => [monthStart], [monthStart]);
  const isTaskScope = Number.isInteger(taskId);

  const [sortBy, setSortBy] = useState('status');
  const [statusFilter, setStatusFilter] = useState(null);

  const hasProject = Number.isInteger(projectId);
  const paceSettingsQuery = useProjectPaceSettings(hasProject ? projectId : null, { enabled: open });
  const updatePaceSettings = useUpdateProjectPaceSettings();
  // Pace settings are part of a project's budget tracking: changing them needs Manage Finance.
  const canChangeSettings = usePermissions().canProject(projectId, 'finance');
  const thresholds = useMemo(() => toThresholds(paceSettingsQuery.data), [paceSettingsQuery.data]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftBehind, setDraftBehind] = useState(DEFAULT_TOLERANCE_PCT);
  const [draftAhead, setDraftAhead] = useState(DEFAULT_TOLERANCE_PCT);

  const taskForecast = useTaskHourForecastForMonths(isTaskScope ? taskId : null, months, { enabled: open });
  const taskActuals = useTaskActualsForMonths(isTaskScope ? taskId : null, months, { enabled: open });
  const toForecast = useTaskOrderHourForecastForMonths(projectId, taskOrderId, months, { enabled: open && !isTaskScope });
  const toActuals = useTaskOrderActualsForMonths(projectId, taskOrderId, months, { enabled: open && !isTaskScope });
  const forecastQuery = isTaskScope ? taskForecast : toForecast;
  const actualsQuery = isTaskScope ? taskActuals : toActuals;

  const { data: usersRes } = useUsers({ enabled: open });
  const { data: closeDatesRes } = useCloseDates({ enabled: open });

  const baseRows = useMemo(() => {
    const byId = new Map();
    const collect = (rows, field) => {
      for (const r of Array.isArray(rows) ? rows : []) {
        const uid = Number(r?.userId);
        if (!Number.isInteger(uid)) continue;
        const rec = byId.get(uid) || { userId: uid, name: String(r?.userName || '').trim(), projected: 0, actual: 0 };
        rec[field] = Number(r?.hours?.[monthStart] ?? 0) || 0;
        if (!rec.name && r?.userName) rec.name = String(r.userName).trim();
        byId.set(uid, rec);
      }
    };
    collect(forecastQuery.data?.rows, 'projected');
    collect(actualsQuery.data?.rows, 'actual');
    return Array.from(byId.values()).filter((r) => r.projected > 0 || r.actual > 0);
  }, [actualsQuery.data?.rows, forecastQuery.data?.rows, monthStart]);

  const userIds = useMemo(() => baseRows.map((r) => r.userId), [baseRows]);
  const timeOffQueries = useUsersTimeOffDays(userIds, { enabled: open });
  const timeOffLoading = timeOffQueries.some((q) => q?.isLoading);
  const timeOffFailed = timeOffQueries.some((q) => q?.error);

  const usersById = useMemo(() => {
    const list = Array.isArray(usersRes) ? usersRes : usersRes?.users;
    const map = new Map();
    for (const u of Array.isArray(list) ? list : []) {
      const id = Number(u?.id);
      if (Number.isInteger(id)) map.set(id, u);
    }
    return map;
  }, [usersRes]);

  const workingDays = useMemo(() => {
    const list = Array.isArray(closeDatesRes) ? closeDatesRes : closeDatesRes?.rows;
    return buildWorkingDays(monthStart, list);
  }, [closeDatesRes, monthStart]);

  const todayIso = toIsoDay(new Date());
  const elapsedDays = workingDays.filter((d) => d < todayIso).length;
  const teamElapsedFraction = workingDays.length > 0 ? elapsedDays / workingDays.length : 0;

  const rows = useMemo(() => {
    return baseRows.map((r, idx) => {
      const user = usersById.get(r.userId);
      const rawFte = Number(user?.ftePercent ?? user?.fte_percent ?? 100);
      const fteScale = Number.isFinite(rawFte) ? Math.max(0, rawFte) / 100 : 1;
      const timeOffByDay = buildTimeOffHoursByDay(timeOffQueries[idx]?.data);
      return {
        ...r,
        name: r.name || String(user?.name || '').trim() || `User ${r.userId}`,
        pace: computePace({ ...r, workingDays, todayIso, timeOffByDay, fteScale, teamElapsedFraction, isPastMonth, thresholds }),
      };
    });
    // timeOffQueries is a fresh array every render; its data is what matters.
  }, [baseRows, usersById, workingDays, todayIso, teamElapsedFraction, isPastMonth, thresholds, timeOffQueries.map((q) => q?.dataUpdatedAt).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const team = useMemo(() => {
    const planned = rows.filter((r) => r.projected > 0);
    const sum = (list, fn) => list.reduce((acc, r) => acc + fn(r), 0);
    const projected = sum(planned, (r) => r.projected);
    const actual = sum(rows, (r) => r.actual);
    const plannedActual = sum(planned, (r) => r.actual);
    const expected = sum(planned, (r) => r.pace.expected);
    const pace = expected > 0 ? plannedActual / expected : null;
    let tone = 'neutral';
    if (pace !== null) tone = pace < thresholds.behindBelow || pace > thresholds.aheadAbove ? 'warn' : 'good';
    return {
      projected,
      actual,
      pace,
      tone,
      complete: projected > 0 ? plannedActual / projected : 0,
      low: sum(planned, (r) => r.pace.low) + sum(rows.filter((r) => r.projected <= 0), (r) => r.actual),
      high: sum(planned, (r) => r.pace.high) + sum(rows.filter((r) => r.projected <= 0), (r) => r.actual),
    };
  }, [rows, thresholds]);

  const statusCounts = useMemo(() => {
    const counts = {};
    for (const r of rows) counts[r.pace.status.key] = (counts[r.pace.status.key] || 0) + 1;
    return counts;
  }, [rows]);

  // One scale for every bar so the 100% line and the "expected by now" ticks line up
  // down the column. Extends past 100% only as far as someone's actual or projection does.
  const domain = useMemo(() => {
    let max = 1.2;
    for (const r of rows) {
      if (r.projected <= 0) continue;
      max = Math.max(max, r.actual / r.projected, r.pace.high / r.projected);
    }
    // Round up to a whole tick step so the last scale label sits at the bar's end.
    const step = max > 1.5 ? 0.5 : 0.25;
    return Math.min(2, Math.ceil(max / step) * step);
  }, [rows]);

  const ticks = useMemo(() => {
    const out = [];
    const step = domain > 1.5 ? 0.5 : 0.25;
    for (let t = 0; t <= domain + 1e-9; t += step) out.push(t);
    return out;
  }, [domain]);

  const visibleRows = useMemo(() => {
    const list = statusFilter ? rows.filter((r) => r.pace.status.key === statusFilter) : [...rows];
    const byName = (a, b) => a.name.localeCompare(b.name);
    const paceOf = (r) => (r.pace.pace === null ? Number.POSITIVE_INFINITY : r.pace.pace);
    const offPace = (r) => (r.pace.pace === null ? 0 : Math.abs(r.pace.pace - 1));
    const sorters = {
      // Within a status, furthest from pace first, whichever direction.
      status: (a, b) => a.pace.status.rank - b.pace.status.rank || offPace(b) - offPace(a) || byName(a, b),
      pace: (a, b) => paceOf(a) - paceOf(b) || byName(a, b),
      remaining: (a, b) => b.pace.remainingToLog - a.pace.remainingToLog || byName(a, b),
      name: byName,
    };
    return list.sort(sorters[sortBy] || sorters.status);
  }, [rows, sortBy, statusFilter]);

  const loading = forecastQuery.isLoading || actualsQuery.isLoading;
  const loadError = forecastQuery.error || actualsQuery.error;
  const updatedAt = formatUpdatedAt(actualsQuery.data?.monthLastModifiedByMonth?.[monthStart]);
  const remainingWorkingDays = workingDays.length - elapsedDays;

  const changeMonth = (next) => {
    setMonthStart(next);
    setStatusFilter(null);
  };

  const openSettings = () => {
    setDraftBehind(thresholds.behindPct);
    setDraftAhead(thresholds.aheadPct);
    updatePaceSettings.reset();
    setSettingsOpen(true);
  };

  const draftValid = (v, max) => Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= max;
  const canSaveDraft = hasProject && draftValid(draftBehind, 100) && draftValid(draftAhead, 500);

  const saveSettings = async (body) => {
    try {
      await updatePaceSettings.mutateAsync({ projectId, ...body });
      setSettingsOpen(false);
    } catch {
      // The error renders inside the settings panel.
    }
  };

  const handleClose = () => {
    setStatusFilter(null);
    setSettingsOpen(false);
    setMonthStart(currentMonthStart);
    onClose?.();
  };

  return (
    <Modal open={open} onClose={handleClose} size="lg" overflow className="ppc-pace-modal">
      <Modal.Header>
        <Modal.Title>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-speedometer2" aria-hidden="true" />
            Pace
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="ppc-pace">
          <div className="ppc-pace__monthbar">
            <div className="ppc-pace-monthnav" role="group" aria-label="Month">
              <button type="button" className="ppc-pace-monthnav__btn" onClick={() => changeMonth(addMonthsIso(monthStart, -1))}
                aria-label="Previous month" title="Previous month">
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>
              <span className="ppc-pace-monthnav__label">{formatMonthTitle(monthStart)}</span>
              <button type="button" className="ppc-pace-monthnav__btn" onClick={() => changeMonth(addMonthsIso(monthStart, 1))}
                aria-label="Next month" title="Next month">
                <i className="bi bi-chevron-right" aria-hidden="true" />
              </button>
            </div>
            {!isCurrentMonth ? (
              <button type="button" className="ppc-pace-filter" onClick={() => changeMonth(currentMonthStart)}>
                <i className="bi bi-calendar-event" aria-hidden="true" />
                This month
              </button>
            ) : null}
            <span className="ppc-pace-faint ppc-pace__monthnote">
              {isPastMonth ? 'Finished month: final hours against forecast' : isCurrentMonth ? 'In progress' : 'Upcoming: nothing logged yet'}
            </span>
            {canChangeSettings ? (
              <button
                type="button"
                className={`ppc-pace-filter ppc-pace__settings-btn${settingsOpen ? ' is-active' : ''}`}
                onClick={() => (settingsOpen ? setSettingsOpen(false) : openSettings())}
                disabled={!hasProject}
                aria-expanded={settingsOpen}
                title={
                  !hasProject
                    ? 'Pace settings need a project'
                    : canChangeSettings
                      ? 'Change what counts as on pace for this project'
                      : 'Changing pace settings needs Manage Finance on this project'
                }
              >
                <i className="bi bi-gear" aria-hidden="true" />
                On pace {formatBand(thresholds)}
              </button>
            ) : null}
          </div>

          {settingsOpen ? (
            <div className="ppc-pace-settings">
              <div className="ppc-pace-settings__head">
                <span className="ppc-pace-label">Pace settings for this project</span>
                {paceSettingsQuery.data?.isDefault ? <span className="ppc-pace-faint">Using the default</span> : null}
              </div>
              <div className="ppc-pace-settings__sentence">
                Count someone as on pace when their logged hours are within
                <InputNumber size="sm" min={0} max={100} step={1} value={draftBehind} onChange={(v) => setDraftBehind(v)}
                  suffix="%" className="ppc-pace-settings__input" aria-label="Tolerance below expected" />
                below and
                <InputNumber size="sm" min={0} max={500} step={1} value={draftAhead} onChange={(v) => setDraftAhead(v)}
                  suffix="%" className="ppc-pace-settings__input" aria-label="Tolerance above expected" />
                above where they should be today.
              </div>
              <div className="ppc-pace-settings__preview">
                {canSaveDraft ? (
                  <>
                    <span><i className="bi bi-chevron-double-down ppc-pace-text--warn" aria-hidden="true" /> Under pace below {Math.round(100 - Number(draftBehind))}%</span>
                    <span><i className="bi bi-check-circle ppc-pace-text--good" aria-hidden="true" /> On pace {Math.round(100 - Number(draftBehind))}–{Math.round(100 + Number(draftAhead))}%</span>
                    <span><i className="bi bi-chevron-double-up ppc-pace-text--warn" aria-hidden="true" /> Over pace above {Math.round(100 + Number(draftAhead))}%</span>
                  </>
                ) : (
                  <span className="ppc-pace-text--danger">Enter 0–100% below and 0–500% above.</span>
                )}
              </div>
              {updatePaceSettings.isError ? (
                <Message type="error" showIcon>
                  {String(updatePaceSettings.error?.message || 'Could not save pace settings.').replace(/^HTTP \d+:\s*/, '')}
                </Message>
              ) : null}
              <div className="ppc-pace-settings__actions">
                <Button
                  appearance="link"
                  size="sm"
                  className="ppc-pace-settings__reset"
                  onClick={() => saveSettings({ reset: true })}
                  disabled={updatePaceSettings.isPending || paceSettingsQuery.data?.isDefault}
                >
                  Reset to default (±{DEFAULT_TOLERANCE_PCT}%)
                </Button>
                <Button
                  appearance="primary"
                  size="sm"
                  loading={updatePaceSettings.isPending}
                  disabled={!canSaveDraft || updatePaceSettings.isPending}
                  onClick={() => saveSettings({ behindTolerancePct: Number(draftBehind), aheadTolerancePct: Number(draftAhead) })}
                >
                  Save for project
                </Button>
                <Button appearance="subtle" size="sm" onClick={() => setSettingsOpen(false)} disabled={updatePaceSettings.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          <p className="ppc-pace__intro">
            {scopeLabel ? <strong>{scopeLabel}. </strong> : null}
            How much of each person&rsquo;s forecast is logged, compared with how much of the month has passed.
            Time off, company close dates and FTE are taken into account.
            {updatedAt ? ` Actuals last changed ${updatedAt}.` : ''}
          </p>

          {loadError ? (
            <Message type="error" showIcon>Could not load this month&rsquo;s hours: {String(loadError?.message || loadError)}</Message>
          ) : loading ? (
            <div className="ppc-pace__empty"><Loader content="Loading this month…" /></div>
          ) : rows.length === 0 ? (
            <div className="ppc-pace__empty">
              <i className="bi bi-calendar-x" aria-hidden="true" />
              Nobody has forecast or logged hours here for {formatMonthTitle(monthStart)}.
            </div>
          ) : (
            <>
              <div className="ppc-pace__summary">
                <div className="ppc-pace-card ppc-pace-card--hero">
                  <PaceRing elapsed={teamElapsedFraction} complete={team.complete} tone={team.tone} />
                  <div className="ppc-pace-hero__legend">
                    <div className="ppc-pace-hero__item">
                      <span className="ppc-pace-swatch ppc-pace-swatch--elapsed" />
                      <div>
                        <div className="ppc-pace-label">Month elapsed</div>
                        <div className="ppc-pace-hero__value">{formatPct(teamElapsedFraction)}</div>
                        <div className="ppc-pace-sub">{elapsedDays} of {workingDays.length} working days · {remainingWorkingDays} left</div>
                      </div>
                    </div>
                    <div className="ppc-pace-hero__item">
                      <span className={`ppc-pace-swatch ppc-pace-tone--${team.tone}`} />
                      <div>
                        <div className="ppc-pace-label">Forecast logged</div>
                        <div className="ppc-pace-hero__value">{formatPct(team.complete)}</div>
                        <div className="ppc-pace-sub">{formatHrs(team.actual)} of {hrsText(team.projected)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ppc-pace-card">
                  <div className="ppc-pace-label">{isPastMonth ? 'Forecast hit' : 'Team pace'}</div>
                  <div className={`ppc-pace-card__value ppc-pace-text--${team.tone}`}>{team.pace === null ? '—' : formatPct(team.pace)}</div>
                  <div className="ppc-pace-sub">
                    {team.pace === null
                      ? 'No working days have passed yet.'
                      : isPastMonth
                        ? `Finished ${formatPct(Math.abs(1 - team.pace))} ${team.pace < 1 ? 'under' : 'over'} forecast`
                        : team.pace < 1
                          ? `Logging ${formatPct(1 - team.pace)} slower than planned`
                          : `Logging ${formatPct(team.pace - 1)} faster than planned`}
                  </div>
                </div>

                <div className="ppc-pace-card">
                  <div className="ppc-pace-label">{isPastMonth ? 'Final hours' : 'Likely finish'}</div>
                  <div className="ppc-pace-card__value">
                    {isPastMonth ? (
                      formatHrs(team.actual)
                    ) : (
                      <>
                        {formatHrs(team.low)}<span className="ppc-pace-card__dash">–</span>{formatHrs(team.high)}
                      </>
                    )}
                    <span className="ppc-pace-card__unit"> hrs</span>
                  </div>
                  <div className="ppc-pace-sub">
                    vs {hrsText(team.projected)} forecast
                    {team.high < team.projected ? ` · ${formatHrs(team.projected - team.high)}${isPastMonth ? '' : '+'} hrs short` : ''}
                  </div>
                </div>
              </div>

              <div className="ppc-pace__toolbar">
                <div className="ppc-pace-filters" role="group" aria-label="Filter by status">
                  <button type="button" className={`ppc-pace-filter${statusFilter === null ? ' is-active' : ''}`} onClick={() => setStatusFilter(null)}>
                    All <span className="ppc-pace-filter__count">{rows.length}</span>
                  </button>
                  {STATUS_ORDER.filter((s) => statusCounts[s.key]).map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      className={`ppc-pace-filter${statusFilter === s.key ? ' is-active' : ''}`}
                      onClick={() => setStatusFilter(statusFilter === s.key ? null : s.key)}
                    >
                      <i className={`bi ${s.icon} ppc-pace-text--${s.tone}`} aria-hidden="true" />
                      {s.label} <span className="ppc-pace-filter__count">{statusCounts[s.key]}</span>
                    </button>
                  ))}
                </div>
                <SelectPicker
                  size="sm"
                  searchable={false}
                  cleanable={false}
                  data={SORT_OPTIONS}
                  value={sortBy}
                  onChange={(v) => setSortBy(v || 'status')}
                  style={{ width: 200 }}
                />
              </div>

              {timeOffFailed ? (
                <Message type="warning" showIcon style={{ marginBottom: 10 }}>
                  Time off could not be loaded for everyone, so some paces assume a full month.
                </Message>
              ) : null}

              <div className={`ppc-pace-table${SHOW_PACE_BARS ? '' : ' ppc-pace-table--no-bars'}`} role="table" aria-label="Pace by person">
                <div className="ppc-pace-table__head" role="row">
                  <div role="columnheader">Person</div>
                  {SHOW_PACE_BARS ? (
                    <div role="columnheader" className="ppc-pace-scale">
                      {ticks.map((t) => (
                        <span key={t} className="ppc-pace-scale__tick" style={{ left: `${(t / domain) * 100}%` }}>
                          {Math.round(t * 100)}%
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div role="columnheader" className="is-num">Logged / Forecast</div>
                  <div role="columnheader" className="is-num">
                    <span className="ppc-pace-head-help">
                      Pace
                      <Whisper
                        placement="top"
                        trigger={['hover', 'focus']}
                        enterable
                        speaker={
                          <Popover title="How pace is worked out" className="ppc-pace-howto">
                            <p>
                              Pace is the share of forecast logged divided by the share of the month elapsed;{' '}
                              {formatPct(thresholds.behindBelow)}–{formatPct(thresholds.aheadAbove)} counts as on pace
                              for this project. Change it with the gear button at the top.
                            </p>
                            <p>
                              The likely finish runs between keeping the current rate and switching to the planned rate
                              for the rest of the month, capped by the time the person has left.
                            </p>
                            <p>
                              &ldquo;Can&rsquo;t reach&rdquo; means their remaining available hours are less than what is
                              left to log.
                            </p>
                          </Popover>
                        }
                      >
                        <button type="button" className="ppc-pace-info ppc-pace-info--inline" aria-label="How pace is worked out">
                          <i className="bi bi-info-circle" aria-hidden="true" />
                        </button>
                      </Whisper>
                    </span>
                  </div>
                  <div role="columnheader" className="is-num">Likely finish</div>
                  <div role="columnheader" className="is-num">Needs</div>
                </div>

                {visibleRows.map((row) => {
                  const p = row.pace;
                  return (
                    <Whisper
                      key={row.userId}
                      placement="top"
                      trigger="hover"
                      delayOpen={150}
                      speaker={<Tooltip className="ppc-pace-tooltip"><RowTooltip row={row} /></Tooltip>}
                    >
                      <div className="ppc-pace-table__row" role="row" tabIndex={0}>
                        <div role="cell" className="ppc-pace-person">
                          <span className="ppc-pace-person__name">{row.name}</span>
                          <StatusChip status={p.status} />
                        </div>
                        {SHOW_PACE_BARS ? (
                          <div role="cell">
                            {row.projected > 0 ? (
                              <PaceBar row={row} domain={domain} />
                            ) : (
                              <div className="ppc-pace-bar__none">No forecast this month</div>
                            )}
                          </div>
                        ) : null}
                        <div role="cell" className="is-num">
                          <div className="ppc-pace-fraction">
                            <span className="ppc-pace-fraction__logged">{formatHrs(row.actual)}</span>
                            <span className="ppc-pace-fraction__sep" aria-hidden="true">/</span>
                            <span className="ppc-pace-fraction__forecast">{hrsText(row.projected)}</span>
                          </div>
                          <div className="ppc-pace-subtext">
                            {row.projected > 0 ? `${formatPct(row.actual / row.projected)} of forecast` : 'No forecast'}
                          </div>
                        </div>
                        <div role="cell" className={`is-num ppc-pace-strong ppc-pace-text--${p.status.tone}`}>
                          {p.pace === null ? '—' : formatPct(p.pace)}
                        </div>
                        <div role="cell" className="is-num">
                          {row.projected > 0 && formatHrs(p.low) !== formatHrs(p.high)
                            ? `${formatHrs(p.low)}–${formatHrs(p.high)}`
                            : formatHrs(p.low)}
                        </div>
                        <div role="cell" className="is-num">
                          {p.neededPerDay ? (
                            <span className={p.neededPerDay > HOURS_PER_DAY ? 'ppc-pace-text--danger' : undefined}>
                              {formatHrs(p.neededPerDay)} hrs/day
                            </span>
                          ) : row.projected > 0 && p.remainingToLog <= 0 ? (
                            <span className="ppc-pace-text--good">Met</span>
                          ) : (
                            <span className="ppc-pace-faint">—</span>
                          )}
                        </div>
                      </div>
                    </Whisper>
                  );
                })}
              </div>

              <div className="ppc-pace__legendbar">
                <div className="ppc-pace-legend" aria-hidden="true">
                  {SHOW_PACE_BARS ? (
                    <>
                      <span><span className="ppc-pace-legend__fill" /> Logged so far</span>
                      <span><span className="ppc-pace-legend__range" /> Likely finish range</span>
                      <span><span className="ppc-pace-legend__expected" /> Where they should be today</span>
                      <span><span className="ppc-pace-legend__target" /> Forecast (100%)</span>
                    </>
                  ) : null}
                  {timeOffLoading ? <span className="ppc-pace-faint">Loading time off…</span> : null}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>

    </Modal>
  );
}
